/*
  This component is a refactor of the original transcript.jsx component, linked below

    https://github.com/WaltonJermaine/platform/blob/e0d95db239880dd35c48f16787fbf1dc001bbb5d/frontend_qa/components/transcription/transcript.jsx

  This new component is a generic, "display a transcript" component that removes
    the pieces of transcript.jsx that were specific to the transcript editing workflow,
    and not specific to just "show me a transcript with audio and text"

    Note that the transcript can still be edited; this is controlled by the `readOnly` prop.

      ^ Although you should read the note below

  We should eventually update transcript.jsx to use this new, generic component.

  Some things to note:

    1) The function `renderAudioControlFooter` is very messy and could most likely be improved

    2) The full functionality of the TranscriptEditor should work (in the sense that events can be edited)

        However, the edit-ability has not been fully vetted, as the implementation that drove this refactor
        makes use of the `readOnly=true` prop.

    3) It would also be a good improvement to update the css of the `renderAudioControlFooter` to be within
      the same horizontal boundary as the `renderTranscript`, so that this entire component is more
      "plug-n-play," in the sense that you can drop it in to any other component in an independent manner
      that doesn't affect the rest of the page.
*/

import React, { Component } from "react";
import Button from "../base-components/button";
import { Radio, RadioGroup } from "@blueprintjs/core";

import AudioScrubber from "../transcription/audioScrubber";
import { Scrubber } from "react-scrubber";
import { soundManager } from "soundmanager2";
import Popup from "../base-components/popup";
import TranscriptEditor from "../transcription/TranscriptEditor";
import {
  EditorState,
  genKey,
  Modifier,
  SelectionState,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import lscache from "lscache";

const TRANSCRIPT_SOUND = "transcript_sound";
const ensureSoundManager = (() => {
  let hasLoaded = false;
  soundManager.onready(() => (hasLoaded = true));
  return f => {
    if (hasLoaded) {
      f();
    } else {
      soundManager.onready(f);
    }
  };
})();

function millisecondsToTimestamp(ms) {
  let numSeconds = Math.floor(ms / 1000);
  let numMinutes = Math.floor(numSeconds / 60);

  numSeconds %= 60;

  if (numSeconds < 10) {
    numSeconds = "0" + numSeconds;
  }

  return numMinutes + ":" + numSeconds;
}

function parseTranscriptSegments(segments) {
  let parsedContent = {
    blocks: [],
    entityMap: {},
  };

  if (!segments) {
    return parsedContent;
  }

  let block = {
    text: "",
    type: segments[0].isCustomer ? "CustomerBubble" : "RepBubble",
    entityRanges: [],
    data: segments[0].metadata,
  };

  segments.forEach(segment => {
    if (block.type === "CustomerBubble" && !segment.isCustomer) {
      parsedContent.blocks.push(block);
      block = {
        text: "",
        type: "RepBubble",
        entityRanges: [],
        data: segment.metadata,
      };
    } else if (block.type === "RepBubble" && segment.isCustomer) {
      parsedContent.blocks.push(block);
      block = {
        text: "",
        type: "CustomerBubble",
        entityRanges: [],
        data: segment.metadata,
      };
    }

    segment.content.forEach(content => {
      if (content.type === "text") {
        block.text = `${block.text} ${content.content}`;
      } else if (content.type === "play") {
        let ts = millisecondsToTimestamp(content.time);
        let entityKey = genKey();

        block.text = `${block.text} ${ts}`;

        block.entityRanges.push({
          offset: block.text.length - ts.length,
          length: ts.length,
          key: entityKey,
        });

        parsedContent.entityMap[entityKey] = {
          type: "TOKEN",
          mutability: "IMMUTABLE",
          data: {
            playing: false,
            timestampMS: content.time,
          },
        };
      }
    });
  });

  parsedContent.blocks.push(block);

  return parsedContent;
}

// a quadratic for the input curve of audio playback speed
const userSpeedToAudioSpeed = input => {
  return input * input + input * 0.5 + 0.5;
};

// inverse of above function
const audioSpeedToUserSpeed = input => {
  return -0.25 + 0.25 * Math.sqrt(16 * input - 7);
};

class TranscriptBase extends Component {
  boundHandleKeyDown = this.handleKeyDown.bind(this);

  constructor(props) {
    super(props);

    this.initialSegments = parseTranscriptSegments(this.props.segments);

    // Remove old cached content states. NOTE: If we start using lscache in other modules, move this somewhere central.
    lscache.flushExpired();

    let persistedContentStateID =
      "contentState-" + this.props.transcription.display_id;
    let contentStateSerialized = lscache.get(persistedContentStateID);

    this.state = {
      timestamp: 0.0,
      isPlaying: false,
      soundUpdateInterval: null,
      manualTimestamp: false,
      playbackSpeed: 1.0,
      windowScrollDirection: -1, // -1 = down, 1 = up
      playMode: "snippet",
      isLooping: false,
      persistedContentStateID: persistedContentStateID,
      playingSegmentTime: null,
      manualSegment: false,
      showShortcutsModal: false,
      removedSegments: false,
      editorState: EditorState.createEmpty(),
      cachedContentState:
        contentStateSerialized !== null
          ? convertFromRaw(JSON.parse(contentStateSerialized))
          : null,
    };
  }

  getCurrentSubSegmentTime() {
    const { segments } = this.props;
    const { timestamp } = this.state;
    const msTimestamp = timestamp * 1000.0;
    let nextTime = null;
    for (let i = segments.length - 1; i >= 0; --i) {
      for (let j = segments[i].content.length - 1; j >= 0; --j) {
        const item = segments[i].content[j];
        if (item.type !== "play") {
          continue;
        }
        if (
          item.time <= msTimestamp &&
          (nextTime == null || nextTime > msTimestamp)
        ) {
          return item.time;
        }
        nextTime = item.time;
      }
    }
    return null;
  }

  handleKeyDown(e) {
    const { transcription } = this.props;

    const { timestamp, isPlaying, playbackSpeed } = this.state;

    if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
      const hotkeys = {
        F7: () =>
          this.setState({
            timestamp: Math.max(0.0, timestamp - 5.0),
            manualTimestamp: true,
          }),
        Escape: () => this.setState({ isPlaying: !isPlaying }),
        F9: () =>
          this.setState({
            timestamp: Math.min(timestamp + 5.0, transcription.call_length_sec),
            manualTimestamp: true,
          }),
        F2: () => {
          const targetUserSpeed = Math.max(
            audioSpeedToUserSpeed(playbackSpeed) - 0.1,
            0.0
          );
          this.setState({
            playbackSpeed: userSpeedToAudioSpeed(targetUserSpeed),
          });
        },
        F3: () => {
          const targetUserSpeed = Math.min(
            audioSpeedToUserSpeed(playbackSpeed) + 0.1,
            1.0
          );
          this.setState({
            playbackSpeed: userSpeedToAudioSpeed(targetUserSpeed),
          });
        },
      };

      if (hotkeys[e.key]) {
        hotkeys[e.key]();
        e.preventDefault();
      }
    }
  }

  componentDidMount() {
    const { transcription, segmentsRemoved } = this.props;
    if (transcription.display_id === segmentsRemoved) {
      this.setState({ removedSegments: true, playMode: "continuous" });
    }

    ensureSoundManager(() => {
      soundManager.createSound({
        id: TRANSCRIPT_SOUND,
        url: transcription.audio_url,
        onfinish: () => {
          let returnToTime = 0.0;
          if (this.state.playMode === "snippet") {
            returnToTime = this.state.playingSegmentTime / 1000.0;
          }
          this.setState(
            {
              isPlaying: false,
              timestamp: returnToTime,
              manualTimestamp: true,
            },
            () => this.setState({ isPlaying: this.state.isLooping })
          );
        },
        autoLoad: true,
      });
    });

    window.addEventListener("keydown", this.boundHandleKeyDown);

    window.addEventListener("beforeunload", e => {
      const { persistedContentStateID, editorState } = this.state;

      const contentState = editorState.getCurrentContent();
      const contentStateSerialized = JSON.stringify(convertToRaw(contentState));

      // `lscache.set` args are key, value, lifetime in minutes. 60 * 24 sets expiration to 1 day.
      lscache.set(persistedContentStateID, contentStateSerialized, 60 * 24);

      // Per MDN, Chrome requires returnValue to be set:
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
      e.preventDefault();
      e.returnValue = "";
    });
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.boundHandleKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    let sound = soundManager.getSoundById(TRANSCRIPT_SOUND);

    if (!prevState.isPlaying && this.state.isPlaying) {
      sound.play();
      this.setState({
        soundUpdateInterval: setInterval(() => {
          this.setState({
            timestamp: sound.position / 1000.0,
            manualTimestamp: false,
          });
        }, 500),
      });
    } else if (prevState.isPlaying && !this.state.isPlaying) {
      sound.pause();
      let { soundUpdateInterval } = this.state;
      clearInterval(soundUpdateInterval);
      this.setState({ soundUpdateInterval: null });
    }

    if (prevState.timestamp !== this.state.timestamp) {
      if (this.state.manualTimestamp) {
        sound.setPosition(this.state.timestamp * 1000.0);
      }
      const currentTime = this.getCurrentSubSegmentTime();
      if (
        currentTime != null &&
        currentTime !== this.state.playingSegmentTime
      ) {
        this.setState({
          playingSegmentTime: currentTime,
          manualSegment: this.state.manualTimestamp,
        });
      }
    }

    if (prevState.playbackSpeed !== this.state.playbackSpeed) {
      sound.setPlaybackRate(this.state.playbackSpeed);
    }

    if (prevState.playingSegmentTime !== this.state.playingSegmentTime) {
      if (this.state.manualSegment) {
        this.setState({ manualSegment: false });
      } else {
        if (this.state.playMode === "snippet") {
          const previousTime = prevState.playingSegmentTime;
          const targetTimestamp = previousTime / 1000.0;
          setTimeout(() => {
            // clear event queue
            if (this.state.isLooping) {
              this.setState({
                timestamp: targetTimestamp,
                manualTimestamp: true,
              });
            } else {
              this.setState({
                isPlaying: false,
                timestamp: targetTimestamp,
                manualTimestamp: true,
              });
            }
          }, 0);
        }
      }
    }
  }

  changeAudioSpeed = () => {
    const { audioSpeed } = this.state;
    const newSpeed = (audioSpeed >= 2 ? 2 : audioSpeed) + 0.25;

    const updateAudio = () => (this.$audio.playbackRate = newSpeed);
    this.setState({ audioSpeed: newSpeed }, updateAudio);
  };

  removeSnippetButtons() {
    const { editorState } = this.state;
    let contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    blocks.forEach(contentBlock => {
      let offset = 0;

      contentBlock.findEntityRanges(
        character => {
          const entityKey = character.getEntity();
          if (entityKey === null) {
            return false;
          }
          return (
            contentState.getEntity(entityKey).getMutability() === "IMMUTABLE"
          );
        },
        (start, end) => {
          let selectionState = SelectionState.createEmpty(
            contentBlock.getKey()
          );
          selectionState = selectionState.merge({
            anchorKey: contentBlock.getKey(),
            anchorOffset: start - offset,
            focusKey: contentBlock.getKey(),
            focusOffset: end - offset,
          });

          offset += end - start;

          contentState = Modifier.removeRange(
            contentState,
            selectionState,
            "forward"
          );
        }
      );

      let selectionState = SelectionState.createEmpty(contentBlock.getKey());
      selectionState = selectionState.merge({
        anchorKey: contentBlock.getKey(),
        anchorOffset: 0,
        focusKey: contentBlock.getKey(),
        focusOffset: contentBlock.getText().length,
      });

      let text = contentState.getBlockForKey(contentBlock.getKey()).getText();
      text = text.replace(/ +/g, " ");
      text = text.replace(/[\n]+/g, " ");
      text = text.trim();
      contentState = Modifier.replaceText(contentState, selectionState, text);
    });

    this.setState({ editorState: EditorState.push(editorState, contentState) });
  }

  parseEditorState = () => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    let segments = blocks.map(contentBlock => {
      const textWithSnippets = contentBlock.getText();
      let textWithoutSnippets = "";
      let lastEnd = 0;

      contentBlock.findEntityRanges(
        character => {
          const entityKey = character.getEntity();
          if (entityKey === null) {
            return false;
          }
          return (
            contentState.getEntity(entityKey).getMutability() === "IMMUTABLE"
          );
        },
        (start, end) => {
          textWithoutSnippets += textWithSnippets.slice(lastEnd, start);
          lastEnd = end;
        }
      );

      textWithoutSnippets += textWithSnippets.slice(
        lastEnd,
        textWithSnippets.length
      );

      textWithoutSnippets = textWithoutSnippets.replace(/ +/g, " ");
      textWithoutSnippets = textWithoutSnippets.replace(/[\n]+/g, " ");
      textWithoutSnippets = textWithoutSnippets.trim();

      let isCustomer = null;
      if (contentBlock.getType() === "CustomerBubble") {
        isCustomer = true;
      } else if (contentBlock.getType() === "RepBubble") {
        isCustomer = false;
      }

      return {
        content: textWithoutSnippets,
        isCustomer: isCustomer,
        metadata: Object.fromEntries(contentBlock.getData()),
      };
    });

    return segments;
  };

  renderShortcutsPopup = () => {
    const hotkeys = {
      "Play/pause audio": "ESC",
      "Fast forward 5s": "F9",
      "Rewind 5s": "F7",
      "Increase playback speed": "F3",
      "Decrease playback speed": "F2",
      "Next segment": "CTRL + ↑",
      "Previous segment": "CTRL + ↓",
    };

    const hotkeyDivs = Object.entries(hotkeys).map(
      ([description, hotkey], i) => (
        <div key={i} className="transcript-popup-shortcuts-row">
          <div className="transcript-popup-shortcuts-label">{description}</div>
          <div className="transcript-popup-shortcuts-keys">
            {hotkey
              .split(" + ")
              .map((key, j) => (
                <div
                  className="transcript-popup-shortcuts-key"
                  key={`k-${i}-${j}`}
                >
                  {key}
                </div>
              ))
              .reduce((result, item) => (
                <React.Fragment>
                  {result} + {item}
                </React.Fragment>
              ))}
          </div>
        </div>
      )
    );

    return (
      <Popup
        className="transcript-popup-shortcuts"
        closePopup={() => this.setState({ showShortcutsModal: false })}
        content={
          <div className="transcript-popup-shortcuts-content">{hotkeyDivs}</div>
        }
      />
    );
  };

  renderTranscript = () => {
    const {
      readOnly,
      SpeakerBubbleComponent,
      SpeakerBubbleBlockProps,
      transcription,
    } = this.props;

    const { timestamp, isPlaying } = this.state;

    return (
      <TranscriptEditor
        readOnly={readOnly}
        highlightPreviousPasses={this.props.highlightPreviousPasses}
        SpeakerBubbleComponent={SpeakerBubbleComponent}
        SpeakerBubbleBlockProps={SpeakerBubbleBlockProps}
        rawContent={this.initialSegments}
        seek={(time, isPlaying) => {
          this.setState({ timestamp: time, manualTimestamp: true }, () =>
            this.setState({ isPlaying: isPlaying })
          );
        }}
        onChange={editorState => this.setState({ editorState: editorState })}
        editorState={this.state.editorState}
        cachedContentState={this.state.cachedContentState}
        hasEvents={transcription.has_events}
        timestamp={timestamp}
        isPlaying={isPlaying}
      />
    );
  };

  renderAudioControlFooter = () => {
    const { transcription } = this.props;
    const {
      timestamp,
      isPlaying,
      playbackSpeed,
      playMode,
      isLooping,
      removedSegments,
      showShortcutsModal,
    } = this.state;

    return (
      <>
        <div className="transcript-footer-scrubber">
          <AudioScrubber
            current={timestamp}
            maximum={transcription.call_length_sec}
            updateCurrent={value =>
              this.setState({ timestamp: value, manualTimestamp: true })
            }
          />
        </div>
        <div className="transcript-footer-skips">
          <Button
            className="transcript-button"
            onClick={() =>
              this.setState({ timestamp: 0.0, manualTimestamp: true })
            }
          >
            <div className="transcript-button-icon">
              <svg
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 8.36602C1.83333 7.98112 1.83333 7.01887 2.5 6.63397L10.75 1.87083C11.4167 1.48593 12.25 1.96706 12.25 2.73686L12.25 12.2631C12.25 13.0329 11.4167 13.5141 10.75 13.1292L2.5 8.36602Z"
                  fill="#112650"
                />
                <rect y="2" width="2" height="12" rx="1" fill="#112650" />
              </svg>
            </div>
            <div className="transcript-button-text">Beg</div>
          </Button>
          <Button
            className="transcript-button transcript-button-gap"
            onClick={() =>
              this.setState({
                timestamp: Math.max(0.0, timestamp - 5.0),
                manualTimestamp: true,
              })
            }
          >
            <div className="transcript-button-icon">
              <svg
                width="26"
                height="15"
                viewBox="0 0 26 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8.36602C11.3333 7.98112 11.3333 7.01887 12 6.63397L20.25 1.87083C20.9167 1.48593 21.75 1.96706 21.75 2.73686L21.75 12.2631C21.75 13.0329 20.9167 13.5141 20.25 13.1292L12 8.36602Z"
                  fill="#112650"
                />
                <path
                  d="M1.5 8.36602C0.833332 7.98112 0.833333 7.01887 1.5 6.63397L9.75 1.87083C10.4167 1.48593 11.25 1.96706 11.25 2.73686L11.25 12.2631C11.25 13.0329 10.4167 13.5141 9.75 13.1292L1.5 8.36602Z"
                  fill="#112650"
                />
              </svg>
            </div>
            <div className="transcript-button-text">Rew -5</div>
          </Button>
          <Button
            className="transcript-button-gap transcript-dark-button transcript-dark-button-wide"
            onClick={() => this.setState({ isPlaying: !isPlaying })}
          >
            <div className="transcript-footer-skips-play-icon">
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isPlaying ? (
                  <React.Fragment>
                    <rect width="5" height="14" rx="1" fill="white" />
                    <rect x="8" width="5" height="14" rx="1" fill="white" />
                  </React.Fragment>
                ) : (
                  <path
                    d="M13.5 7.13397C14.1667 7.51887 14.1667 8.48112 13.5 8.86602L1.5 15.7942C0.833333 16.1791 -7.73604e-07 15.698 -7.39955e-07 14.9282L-1.34273e-07 1.0718C-1.00623e-07 0.301996 0.833333 -0.17913 1.5 0.20577L13.5 7.13397Z"
                    fill="white"
                  />
                )}
              </svg>
            </div>
            <div className="transcript-footer-skips-play-text">
              {isPlaying ? "Pause" : "Play"}
            </div>
          </Button>
          <Button
            className="transcript-button transcript-button-gap"
            onClick={() =>
              this.setState({
                timestamp: Math.min(
                  transcription.call_length_sec,
                  timestamp + 5.0
                ),
                manualTimestamp: true,
              })
            }
          >
            <div className="transcript-button-icon">
              <svg
                width="26"
                height="15"
                viewBox="0 0 26 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                transform="rotate(180)"
              >
                <path
                  d="M12 8.36602C11.3333 7.98112 11.3333 7.01887 12 6.63397L20.25 1.87083C20.9167 1.48593 21.75 1.96706 21.75 2.73686L21.75 12.2631C21.75 13.0329 20.9167 13.5141 20.25 13.1292L12 8.36602Z"
                  fill="#112650"
                />
                <path
                  d="M1.5 8.36602C0.833332 7.98112 0.833333 7.01887 1.5 6.63397L9.75 1.87083C10.4167 1.48593 11.25 1.96706 11.25 2.73686L11.25 12.2631C11.25 13.0329 10.4167 13.5141 9.75 13.1292L1.5 8.36602Z"
                  fill="#112650"
                />
              </svg>
            </div>
            <div className="transcript-button-text">Fwd +5</div>
          </Button>
        </div>

        <div className="transcript-footer-speed">
          <div className="transcript-footer-speed-title">PLAYBACK SPEED</div>
          <Scrubber
            className="transcript-footer-speed-scrubber"
            min={0.0}
            max={1.0}
            value={audioSpeedToUserSpeed(playbackSpeed)}
            onScrubEnd={value =>
              this.setState({ playbackSpeed: userSpeedToAudioSpeed(value) })
            }
            onScrubChange={value =>
              this.setState({ playbackSpeed: userSpeedToAudioSpeed(value) })
            }
          />

          <div className="transcript-footer-speed-marks">
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark" />
            <div className="transcript-footer-speed-mark transcript-footer-speed-mark-end" />
          </div>

          <div className="transcript-footer-speed-labels">
            <span className="transcript-footer-speed-labels-slower">
              Slower
            </span>
            <span className="transcript-footer-speed-labels-normal">
              Normal
            </span>
            <span className="transcript-footer-speed-labels-faster">
              Faster
            </span>
          </div>
        </div>

        <div className="transcript-footer-button transcript-footer-button-radios">
          <RadioGroup
            selectedValue={playMode}
            onChange={e => {
              this.setState({
                playMode: removedSegments ? "continuous" : e.target.value,
              });
            }}
          >
            <Radio label="Play Snippet" value="snippet" />
            <Radio label="Play Continuous" value="continuous" />
          </RadioGroup>
        </div>

        <div
          className="transcript-footer-button transcript-footer-button-clickable"
          onClick={() => this.setState({ isLooping: !isLooping })}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0)">
              <path
                d="M0.75 3V7.5H5.25"
                stroke="#576984"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.25 15V10.5H12.75"
                stroke="#576984"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.3675 6.75C14.9871 5.67508 14.3407 4.71405 13.4884 3.95656C12.6361 3.19907 11.6059 2.66982 10.4938 2.41819C9.38167 2.16656 8.22393 2.20075 7.12861 2.51758C6.03328 2.8344 5.03606 3.42353 4.23 4.23L0.75 7.5M17.25 10.5L13.77 13.77C12.9639 14.5765 11.9667 15.1656 10.8714 15.4824C9.77607 15.7992 8.61833 15.8334 7.50621 15.5818C6.3941 15.3302 5.36385 14.8009 4.5116 14.0434C3.65935 13.2859 3.01288 12.3249 2.6325 11.25"
                stroke="#576984"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <div className="transcript-footer-button-text">
            Loop:{" "}
            {isLooping ? (
              <span className="transcript-footer-button-text-on">On</span>
            ) : (
              <span className="transcript-footer-button-text-off">Off</span>
            )}
          </div>
        </div>

        <div
          className="transcript-footer-button transcript-footer-button-shortcuts transcript-footer-button-clickable"
          onClick={() => this.setState({ showShortcutsModal: true })}
        >
          <div className="transcript-footer-button-text">See Shortcuts</div>
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 7L7 1L1 7"
              stroke="#576984"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {showShortcutsModal && this.renderShortcutsPopup()}
      </>
    );
  };

  render() {
    return (
      <div>
        <div>{this.renderTranscript()}</div>

        <div className="transcript-footer">
          {this.renderAudioControlFooter()}
        </div>
      </div>
    );
  }
}

export default TranscriptBase;
